import React, { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Collapse,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material'
import { Refresh, ExpandMore, ExpandLess } from '@mui/icons-material'
import {
  getRatingSnapshots,
  getRatingSnapshotDetail
} from '../../../../lib/requests.es6.js'

const navy = '#173E4B'
const ADD_ONS_KEY = '__add_ons__' // packageCode=null lane, keyed distinctly from any real code

const TH = {
  fontSize: 11,
  fontWeight: 700,
  color: '#6B7280',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  bgcolor: '#F9FAFB',
  borderBottom: '1px solid #E5E7EB'
}

function fmtDate(d) {
  if (!d) return 'N/A'
  const dt = new Date(d)
  if (Number.isNaN(dt.getTime())) return String(d)
  return dt.toLocaleString()
}

// Exported (not just used internally) so its collision-avoidance behavior and
// the lane-merge logic below can be unit tested directly, without needing to
// trigger this component's useEffect — which this project's Enzyme `shallow`
// setup does not execute.
export function rowKey(s) {
  return `${s.package_code || ADD_ONS_KEY}:${s.rating_version}`
}

// Merges the settled per-lane list calls into one table, newest first.
// allSettled (not all) is deliberate: one stale/erroring package lane (a
// renamed or deleted package code, a transient backend blip) must not blank
// out the rest of the product's rating history that DID load successfully.
export function mergeSettledSnapshotLanes(settledResults, laneCount) {
  const list = settledResults
    .filter(r => r.status === 'fulfilled')
    .reduce((acc, r) => acc.concat(r.value), [])
    .sort((a, b) => new Date(b.captured_at) - new Date(a.captured_at))

  const failures = settledResults.filter(r => r.status === 'rejected')
  let error = null
  if (failures.length > 0) {
    const e = failures[0].reason
    const message =
      (e && e.response && e.response.data && e.response.data.message) ||
      (e && e.message) ||
      'Failed to load rating snapshots'
    error =
      failures.length < laneCount
        ? `${message} (showing ${laneCount -
            failures.length} of ${laneCount} package lanes)`
        : message
  }
  return { list, error }
}

// Read-only rating history — versioned separately from the product's own
// version/snapshot history (see SnapshotsPanel). Rating history is scoped per
// (product, package) on the backend — packageCode omitted means the
// product's add-ons, NOT "every package" — so this fans out one list call
// per package code plus one for add-ons, and merges them into one table with
// a Package column, mirroring how the bulk-import report groups by package.
function RatingSnapshotsPanel(props) {
  const { productGuid, packageCodes } = props
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [list, setList] = useState([])
  const [openKey, setOpenKey] = useState(null)
  const [details, setDetails] = useState({})

  const load = useCallback(() => {
    if (!productGuid) return
    setLoading(true)
    setError(null)
    const lanes = (packageCodes || []).concat([null])
    // allSettled, not all — see mergeSettledSnapshotLanes: one stale/erroring
    // lane must not blank out the rest of the history that DID load.
    Promise.allSettled(
      lanes.map(code =>
        getRatingSnapshots(productGuid, code).then(res =>
          (Array.isArray(res.data) ? res.data : []).map(s => ({
            ...s,
            package_code: code
          }))
        )
      )
    ).then(results => {
      const { list: merged, error: mergeError } = mergeSettledSnapshotLanes(
        results,
        lanes.length
      )
      setList(merged)
      if (mergeError) setError(mergeError)
      setLoading(false)
    })
  }, [productGuid, packageCodes])

  useEffect(() => {
    load()
  }, [load])

  const toggle = s => {
    const key = rowKey(s)
    if (openKey === key) {
      setOpenKey(null)
      return
    }
    setOpenKey(key)
    if (!details[key]) {
      setDetails(d => ({ ...d, [key]: { loading: true } }))
      getRatingSnapshotDetail(productGuid, s.rating_version, s.package_code)
        .then(res =>
          setDetails(d => ({ ...d, [key]: { loading: false, data: res.data } }))
        )
        .catch(e =>
          setDetails(d => ({
            ...d,
            [key]: {
              loading: false,
              error:
                (e &&
                  e.response &&
                  e.response.data &&
                  e.response.data.message) ||
                e.message ||
                'Failed to load snapshot'
            }
          }))
        )
    }
  }

  const renderDetail = s => {
    const entry = details[rowKey(s)]
    if (!entry) return null
    if (entry.loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} sx={{ color: navy }} />
        </Box>
      )
    }
    if (entry.error) {
      return (
        <Alert severity="error" sx={{ my: 1 }}>
          {entry.error}
        </Alert>
      )
    }
    const snap = entry.data || {}
    return (
      <Box sx={{ py: 1.5 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: '#6B7280',
            textTransform: 'uppercase',
            letterSpacing: '0.04em'
          }}
        >
          Captured rating nodes + referenced tables
        </Typography>
        <Box
          component="pre"
          sx={{
            mt: 0.5,
            m: 0,
            p: 1.5,
            bgcolor: '#0f172a',
            color: '#e2e8f0',
            borderRadius: 1,
            fontSize: 11.5,
            lineHeight: 1.5,
            maxHeight: 320,
            overflow: 'auto'
          }}
        >
          {JSON.stringify(snap.payload != null ? snap.payload : snap, null, 2)}
        </Box>
      </Box>
    )
  }

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: '#1f2937' }}
            >
              Rating History
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Rating-only version history, per package — separate from the
              product's own version, so pricing approvals never mix with
              product-structure ones.
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                onClick={load}
                disabled={loading}
                sx={{ color: navy }}
              >
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: navy }} />
          </Box>
        ) : list.length === 0 && !error ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic' }}
          >
            No rating snapshots yet — they are captured when an approved rating
            change takes effect.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={TH}>Package</TableCell>
                  <TableCell sx={TH}>Rating Version</TableCell>
                  <TableCell sx={TH}>Captured</TableCell>
                  <TableCell sx={TH} align="right">
                    Snapshot
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map(s => {
                  const key = rowKey(s)
                  const open = openKey === key
                  return (
                    <React.Fragment key={key}>
                      <TableRow hover>
                        <TableCell sx={{ color: '#374151' }}>
                          {s.package_code || 'Add-ons'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={`v${s.rating_version}`}
                            sx={{
                              fontWeight: 600,
                              bgcolor: '#eef2f4',
                              color: navy
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#374151' }}>
                          {fmtDate(s.captured_at)}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={() => toggle(s)}
                            endIcon={open ? <ExpandLess /> : <ExpandMore />}
                            sx={{ textTransform: 'none', color: navy }}
                          >
                            {open ? 'Hide' : 'View'}
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{
                            py: 0,
                            borderBottom: open ? undefined : 'none'
                          }}
                        >
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            {renderDetail(s)}
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  )
}

export default RatingSnapshotsPanel
